"use client";
import { useEffect, useState, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { Loader2, X } from "lucide-react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { Product, ProductVariantSize } from "@/app/lib/features/product/product.types";
import FallbackImage from "@/app/components/elements/FallbackImage";
import { Button } from "@/app/components/shadcn/button";
import { cn } from "@/app/utils/cn";
import useCart from "@/app/lib/features/cart/cart.hook";
import { useCreateModal } from "@/app/lib/features/modal/modal.hook";
import { useMountTransition } from "@/app/hooks/useMountTransition";
import { trackProductAddToCart } from "@/app/lib/tracking/interactions";

interface ProductCardMobileCartSheetProps {
  product: Product;
  isOpen: boolean;
  onClose: () => void;
}

interface SheetContentProps {
  product: Product;
  isVisible: boolean;
  onClose: () => void;
}

/**
 * Sheet'in gerçek gövdesi — YALNIZCA açıldığında mount edilir.
 *
 * Buradaki hook'lar (useCart 14 selector, createModal, useRouter, tüm
 * useMemo/useCallback'ler) eskiden sheet kapalıyken de çalışıyordu: erken return
 * hook'ların ARDINDAN geldiği için 84 kartın hepsi bu maliyeti ödüyordu.
 * Dış/iç ayrımıyla maliyet sıfıra iniyor, mount animasyonu ise korunuyor —
 * geçiş durumunu (useMountTransition) dıştaki sarmalayıcı yönetiyor.
 */
const SheetContent = ({ product, isVisible, onClose }: SheetContentProps) => {
  const { addToCart } = useCart();
  const createModal = useCreateModal();
  const router = useRouter();
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [isBuyingNow, setIsBuyingNow] = useState(false);
  const [selectedColorIdx, setSelectedColorIdx] = useState(0);
  const [selectedSizeIdx, setSelectedSizeIdx] = useState(-1);

  const colors = useMemo(() => product.variants_by_color ?? [], [product]);
  const sizes = useMemo<ProductVariantSize[]>(
    () => colors[selectedColorIdx]?.sizes ?? [],
    [colors, selectedColorIdx],
  );

  const handleColorSelect = useCallback(
    (idx: number) => {
      const newSizes = colors[idx]?.sizes ?? [];
      const newDefault = Math.max(newSizes.findIndex((s) => s.stock > 0), 0);
      setSelectedColorIdx(idx);
      setSelectedSizeIdx(newDefault);
    },
    [colors],
  );

  const getVariantId = useCallback(() => {
    const selectedColor = colors[selectedColorIdx];
    if (sizes.length > 0) {
      if (selectedSizeIdx === -1) return undefined;
      return sizes[selectedSizeIdx]?.variant_id;
    }
    return selectedColor?.variant_id ?? product.variant_id;
  }, [colors, selectedColorIdx, sizes, selectedSizeIdx, product]);

  const parsedPrice = useMemo(
    () => parseFloat(product.price.replace(/\./g, "").replace(",", ".")) || 0,
    [product.price],
  );

  const handleAddToCart = useCallback(async () => {
    const variantId = getVariantId();
    if (!variantId) { toast.error("Lütfen beden seçiniz"); return; }

    setIsAddingToCart(true);
    const result = await addToCart(
      { product_id: product.id, variant_id: variantId, quantity: 1 },
      false,
      { name: product.name, price: parsedPrice, brand: product.brand_name },
    );

    if (result.success) {
      trackProductAddToCart(product.id);
      setIsAddingToCart(false);
      onClose();
      createModal("added-to-cart", {
        image: colors[selectedColorIdx]?.primary_image ?? product.images?.[0] ?? "",
        name: product.internet_product_description || product.name,
        colorName: colors.length > 1 ? colors[selectedColorIdx]?.color_name : undefined,
        sizeName: selectedSizeIdx !== -1 ? sizes[selectedSizeIdx]?.size : undefined,
        price: product.price,
      });
    } else {
      toast.error(result.message ?? "Sepete eklenemedi");
      setIsAddingToCart(false);
    }
  }, [getVariantId, product, colors, sizes, selectedColorIdx, selectedSizeIdx, parsedPrice, addToCart, onClose, createModal]);

  const handleBuyNow = useCallback(async () => {
    const variantId = getVariantId();
    if (!variantId) { toast.error("Lütfen beden seçiniz"); return; }

    setIsBuyingNow(true);
    const result = await addToCart(
      { product_id: product.id, variant_id: variantId, quantity: 1 },
      false,
      { name: product.name, price: parsedPrice, brand: product.brand_name },
    );

    if (result.success) {
      onClose();
      router.push("/sepet");
    } else {
      toast.error(result.message ?? "Sepete eklenemedi");
      setIsBuyingNow(false);
    }
  }, [getVariantId, product.id, product.name, product.brand_name, parsedPrice, addToCart, onClose, router]);

  // isVisible, isOpen'ın geçiş süresi eklenmiş hâli: kapanış animasyonu biterken
  // (isVisible=false, bileşen hâlâ mount) scroll kilidi kalkar — eski davranışın
  // aynısı.
  useEffect(() => {
    document.body.style.overflow = isVisible ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isVisible]);

  if (typeof window === "undefined") return null;

  const hasColors = colors.length > 1;
  const hasSizes = sizes.length > 0;
  const productImage = colors[selectedColorIdx]?.primary_image ?? product.images?.[0] ?? "";
  const productName = product.internet_product_description || product.name;
  const selectedSize = selectedSizeIdx !== -1 ? sizes[selectedSizeIdx] : undefined;
  const isOutOfStock = hasSizes && selectedSizeIdx !== -1 && selectedSize?.stock === 0;
  const isAnyLoading = isAddingToCart || isBuyingNow;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[99998] bg-black/50"
        style={{
          opacity: isVisible ? 1 : 0,
          transition: "opacity 200ms ease",
          pointerEvents: isVisible ? "auto" : "none",
        }}
        onClick={onClose}
      />

      {/* Sheet */}
      <div
        className="fixed bottom-0 left-0 right-0 z-[99999] bg-white rounded-t-2xl pb-safe"
        style={{
          transform: isVisible ? "translateY(0)" : "translateY(100%)",
          transition: "transform 300ms ease-out",
          willChange: "transform",
          pointerEvents: isVisible ? "auto" : "none",
        }}
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-gray-300" />
        </div>

        {/* Product info header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b">
          <div className="relative w-14 h-20 rounded-lg overflow-hidden shrink-0 border border-gray-100">
            <FallbackImage
              src={productImage}
              alt={productName}
              fill
              className="object-cover"
              sizes="56px"
            />
          </div>
          <div className="flex-1 min-w-0">
            {product.brand_name && (
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide line-clamp-1">
                {product.brand_name}
              </p>
            )}
            <p className="text-sm font-semibold text-gray-900 line-clamp-2 leading-snug mt-0.5">
              {productName}
            </p>
            <p className="text-sm font-bold text-main mt-1">{product.price}</p>
          </div>
          <button
            onClick={onClose}
            className="self-start p-1.5 rounded-full hover:bg-gray-100 transition-colors shrink-0"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-4 pt-4 pb-2 space-y-4 max-h-[50vh] overflow-y-auto">
          {/* Colors */}
          {hasColors && (
            <div className="space-y-2">
              <p className="text-sm font-medium">
                Renk:{" "}
                <span className="text-main font-semibold">
                  {colors[selectedColorIdx]?.color_name}
                </span>
              </p>
              <div className="flex flex-wrap gap-2">
                {colors.map((color, idx) => (
                  <button
                    key={color.variant_id}
                    type="button"
                    title={color.color_name}
                    onClick={() => handleColorSelect(idx)}
                    className={cn(
                      "relative w-12 h-16 rounded-lg border-2 overflow-hidden transition-all shrink-0",
                      selectedColorIdx === idx
                        ? "border-main shadow-md scale-105"
                        : "border-gray-200 hover:border-gray-400",
                    )}
                  >
                    <FallbackImage
                      src={color.primary_image}
                      alt={color.color_name}
                      fill
                      className="object-cover"
                      sizes="48px"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Sizes */}
          {hasSizes && (
            <div className="space-y-2">
              <p className="text-sm font-medium">
                Beden:{" "}
                {selectedSize && (
                  <span className="text-main font-semibold">{selectedSize.size}</span>
                )}
              </p>
              <div className="flex flex-wrap gap-2">
                {sizes.map((size, idx) => {
                  const isSel = idx === selectedSizeIdx;
                  const oos = size.stock === 0;
                  return (
                    <button
                      key={size.id}
                      type="button"
                      disabled={oos}
                      onClick={() => !oos && setSelectedSizeIdx(idx)}
                      className={cn(
                        "h-9 px-3 min-w-10 border-2 rounded-lg font-medium text-sm transition-all duration-200 flex items-center justify-center",
                        oos
                          ? "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed line-through"
                          : isSel
                            ? "border-main bg-main text-white shadow-md"
                            : "border-gray-300 bg-white text-gray-700 hover:border-main hover:text-main",
                      )}
                    >
                      {size.size}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Bottom action row */}
        {hasSizes && selectedSizeIdx === -1 && (
          <p className="px-4 pb-1 text-xs text-amber-600 font-medium">Lütfen bir beden seçiniz</p>
        )}
        <div className="flex gap-3 px-4 pt-3 pb-6">
          <Button
            variant="outline-main"
            className="flex-1 h-12 text-base border-2"
            onClick={handleBuyNow}
            disabled={isAnyLoading || isOutOfStock || (hasSizes && selectedSizeIdx === -1)}
          >
            {isBuyingNow ? <Loader2 className="h-5 w-5 animate-spin" /> : "Şimdi Al"}
          </Button>
          <Button
            className="flex-1 h-12 text-base"
            onClick={handleAddToCart}
            disabled={isAnyLoading || isOutOfStock || (hasSizes && selectedSizeIdx === -1)}
          >
            {isAddingToCart ? (
              <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Ekleniyor...</>
            ) : (
              "Sepete Ekle"
            )}
          </Button>
        </div>
      </div>
    </>,
    document.body,
  );
};

/**
 * Dış sarmalayıcı — her ProductCard'da mount edilir, bu yüzden TEK hook'u var.
 * Sheet kapalıyken maliyeti bir useState + bir useEffect'ten ibaret.
 */
const ProductCardMobileCartSheet = ({
  product,
  isOpen,
  onClose,
}: ProductCardMobileCartSheetProps) => {
  const { rendered, isVisible } = useMountTransition(isOpen, 300);

  if (!rendered) return null;

  // Sıra korunuyor: rendered=true ile mount → rAF → isVisible=true →
  // CSS transition çalışır (dynamic import ile mount animasyonu bozulurdu).
  return (
    <SheetContent product={product} isVisible={isVisible} onClose={onClose} />
  );
};

export default ProductCardMobileCartSheet;
